// Media uploads that return a plain URL for a caller to store on a text column.
//
// Distinct from the logo/avatar uploads in `useCompanyMutationsQuery` and
// `useUserProfileMutations`, which attach their file to the record's own
// multipart save. Here the upload is its OWN request: the product / company save
// that follows is ordinary JSON carrying the returned string, so a failed upload
// costs the user their FILE and not the form they were filling in — the same
// split `supportApi.uploadAttachment` makes, and for the same reason.

import { createApiCall } from './index'

/**
 * Per-file cap, MB. MUST equal `SCAN_MEDIA_MAX_SIZE_MB` in the API's
 * `domain/constants/scan-media-uploads.ts`, which is the enforcing side — this copy
 * only drives the picker's early refusal and the "(Max file size: N MB)" hint.
 *
 * 8, not 10, and the difference is the whole point: there is no transcoding anywhere in
 * the stack, so whatever a brand uploads is byte-for-byte what a customer downloads on a
 * phone. The server's constant carries the full reasoning.
 *
 * A mismatch here is not cosmetic. `FileUploadField` records what it looks like: its hint
 * once advertised 10MB while the dropzone enforced 2 and the error said something else
 * again, so a 3MB file was refused by a field that had just promised it would be accepted.
 * If the server number changes, change this one in the same commit.
 */
export const SCAN_MEDIA_MAX_SIZE_MB = 8

/**
 * MIME type → extensions, in the shape react-dropzone's `accept` wants.
 * Exported so the field, the endpoint contract and any future call site read one
 * list rather than three copies that drift.
 */
export const SCAN_MEDIA_ACCEPT: Record<string, string[]> = {
  'image/png': ['.png'],
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],
  'video/mp4': ['.mp4'],
  'video/quicktime': ['.mov'],
  'video/webm': ['.webm'],
}

/**
 * Upload one scan-instruction image or video; returns the URL to store on the
 * product's `scan_media_url` (or the company's `default_scan_media_url`).
 *
 * Do NOT set a Content-Type here: `createApiCall` detects FormData and omits its
 * default `application/json` precisely so the browser can generate the multipart
 * boundary (services/index.ts). Setting it by hand produces a boundary-less
 * header and the server reads an empty body.
 */
export const uploadScanMedia = async (file: File): Promise<{ path: string }> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await createApiCall({
    method: 'POST',
    url: '/media/admin/scan-media',
    data: formData,
  })

  const path = (response?.data as { path?: string } | undefined)?.path
  if (!path) {
    // A non-2xx comes back through `errorHandler` as the response object rather
    // than a throw, so an absent `path` — not an exception — is what a failure
    // looks like at this call site.
    throw new Error(
      (response?.data as { message?: string } | undefined)?.message ||
        "Couldn't upload that file",
    )
  }

  return { path }
}

/**
 * Per-file cap, MB. MUST equal `PRODUCT_MODEL_MAX_SIZE_MB` in the API's
 * `domain/constants/product-model-uploads.ts`, which is the enforcing side — this copy
 * only drives the picker's early refusal and the "(Max file size: N MB)" hint.
 *
 * 12 rather than scan media's 8, and the server constant carries the full argument plus
 * an honest account of what the number does NOT fix. The short version: a model is
 * heavier than a photograph because of its textures, but it is also fetched EAGERLY as
 * the certificate hero rather than behind a tap, so the two pressures partly cancel.
 *
 * Same warning as the constant above: a mismatch here is not cosmetic. A hint that
 * promises more than the dropzone enforces refuses a file it has just said is fine.
 */
export const PRODUCT_MODEL_MAX_SIZE_MB = 12

/**
 * GLB only, in react-dropzone's `accept` shape.
 *
 * ⚠️ The extension is listed against `model/gltf-binary` AND `application/octet-stream`
 * on purpose. react-dropzone matches a dropped file on its browser-reported MIME type
 * first, and most operating systems do not register `.glb` — so a genuine model arrives
 * as `application/octet-stream` (or an empty type) and a `model/gltf-binary`-only map
 * would reject it in the picker, before the server ever got the chance to sniff its
 * bytes. The API's own check is the real gate and it verifies the glTF magic AND version
 * word, so widening here loses nothing: a mislabelled non-model is refused server-side
 * with a message naming the fix.
 */
export const PRODUCT_MODEL_ACCEPT: Record<string, string[]> = {
  'model/gltf-binary': ['.glb'],
  'application/octet-stream': ['.glb'],
}

/**
 * Upload one GLB; returns the URL to store on the product's `model_url`.
 *
 * Its own request, like `uploadScanMedia` and for the same reason: the product save that
 * follows is ordinary JSON carrying the returned string, so a failed upload costs the
 * user their FILE and not the form they were filling in.
 *
 * Do NOT set a Content-Type here — see the note on `uploadScanMedia`.
 */
export const uploadProductModel = async (file: File): Promise<{ path: string }> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await createApiCall({
    method: 'POST',
    url: '/media/admin/product-model',
    data: formData,
  })

  const path = (response?.data as { path?: string } | undefined)?.path
  if (!path) {
    // A non-2xx comes back through `errorHandler` as the response object rather than a
    // throw, so an absent `path` — not an exception — is what a failure looks like here.
    throw new Error(
      (response?.data as { message?: string } | undefined)?.message ||
        "Couldn't upload that model",
    )
  }

  return { path }
}

/**
 * VRG-790 — the reveal pair: the video and its required poster frame.
 *
 * MP4 and WebM only, and note `video/quicktime` is deliberately ABSENT where
 * `SCAN_MEDIA_ACCEPT` above includes it. A .mov off an iPhone is the single most likely
 * thing a brand drags in and exactly what must not ship — routinely 50MB for ten
 * seconds, and Safari is the only browser that reliably plays one. Leaving it out of the
 * picker means the file is greyed out in the OS dialog rather than uploaded and then
 * refused, which is a better place to learn it. The server refuses it too, with a
 * message naming the fix (export as MP4), for anything that gets past the picker.
 */
export const REVEAL_VIDEO_ACCEPT: Record<string, string[]> = {
  'video/mp4': ['.mp4', '.m4v'],
  'video/webm': ['.webm'],
}

/** Stills only. A poster that MOVES would be a second reveal competing with the first. */
export const REVEAL_POSTER_ACCEPT: Record<string, string[]> = {
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/webp': ['.webp'],
}

/**
 * Stated in the form BEFORE upload, per the acceptance criteria — a brand should learn
 * the limits while choosing a file, not from a rejection after waiting for one to
 * upload. These must stay in step with `reveal-video-uploads.ts` on the API, which is
 * the enforcing side; these numbers are only the copy.
 */
export const REVEAL_VIDEO_MAX_SIZE_MB = 5
export const REVEAL_VIDEO_MAX_SECONDS = 6
export const REVEAL_POSTER_MAX_SIZE_MB = 2

/**
 * Upload the reveal video; returns the URL for `products.reveal_video_url` plus the
 * duration the SERVER measured.
 *
 * The duration comes back because the server's number is the authoritative one — the
 * form reads `HTMLVideoElement.duration` to fail fast with a good message, but that is a
 * courtesy and the API re-reads it from the container. Showing what the server measured
 * is what stops a brand believing a 6.0s clip was accepted when the container says 6.04.
 *
 * Do NOT set a Content-Type here — see the note on `uploadScanMedia`.
 */
export const uploadRevealVideo = async (
  file: File,
): Promise<{ path: string; duration_seconds: number }> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await createApiCall({
    method: 'POST',
    url: '/media/admin/reveal-video',
    data: formData,
  })

  const data = response?.data as
    | { path?: string; duration_seconds?: number; message?: string }
    | undefined
  if (!data?.path) {
    // A non-2xx arrives through `errorHandler` as the response object rather than a
    // throw, so an absent `path` is what a failure looks like. The server's message is
    // preferred because it names the fix (too long → trim it; .mov → export as MP4).
    throw new Error(data?.message || "Couldn't upload that video")
  }

  return { path: data.path, duration_seconds: data.duration_seconds ?? 0 }
}

/** Upload the reveal's poster frame; returns the URL for `products.reveal_poster_url`. */
export const uploadRevealPoster = async (file: File): Promise<{ path: string }> => {
  const formData = new FormData()
  formData.append('file', file)

  const response = await createApiCall({
    method: 'POST',
    url: '/media/admin/reveal-poster',
    data: formData,
  })

  const data = response?.data as { path?: string; message?: string } | undefined
  if (!data?.path) {
    throw new Error(data?.message || "Couldn't upload that image")
  }

  return { path: data.path }
}
